import { Trans, useLingui } from "@lingui/react/macro";
import { cloneDeep } from "lodash";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { ReactNode, useState } from "react";

import {
  apiPostUsers,
  apiPutUsersUuid,
  LocaleTag,
  PermissionBit,
  UpdateUserAccessPolicySchema,
  UserResponse,
  UserRole,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "../../../components/ui-kit/dialog";
import { PasswordInput } from "../../../components/ui-kit/fields/password-input";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import {
  ADD_DIALOG_CLASS,
  ADD_DIALOG_WIDTH,
  WIDE_DIALOG_CLASS,
} from "../../../components/ui-kit/record-dialog-chrome";
import { Stepper } from "../../../components/ui-kit/stepper";
import { supportedLanguages } from "../../../constants/enum-to-text";
import { createPatch } from "../../../utils/patch";
import { assert } from "../../../utils/utils";
import { AdminConsole } from "../admin-console";
import {
  PERMISSION_SECTIONS_CLASS,
  PermissionsPicker,
} from "../permissions/permissions-picker";
import {
  buildInitialFields,
  type Fields,
  policyPatchBody,
} from "../permissions/policy-fields";
import {
  PermissionsMetadataFallback,
  usePermissionsMetadata,
} from "../permissions/use-permissions-metadata";
import { toLocations } from "../permissions/utils";
import { VisibilityPicker } from "../permissions/visibility-picker";
import { AssignmentRows } from "./assignment-rows";

/**
 * The roles this wizard can actually create - the `connectedRoles` (`patient`
 * and `patient-ppg-app`) are deliberately absent. `POST /users` rejects both
 * without a `connected_patient_id` ("`patient` role must have connected patient
 * ID"), and this wizard has none to give: step 1 creates the account before any
 * record is picked. Patient accounts come from "Add new patient", which records
 * the patient, the consent and the user together.
 *
 * Adding a connected role back therefore means moving a patient picker into
 * step 1, not just extending this list.
 */
const creatableRoles: UserRole[] = [
  UserRole["physician-ppg-app"],
  UserRole["study-ppg-app"],
  UserRole.physician,
  UserRole["study-physician"],
  UserRole["seerlinq-user"],
];

/**
 * Creating a user in three steps: the account, the permissions its role gave
 * it, then the records it is associated with.
 *
 * Step 1 really creates the user, so the later steps write against a real uuid
 * instead of buffering one large submit — which also means leaving after step 1
 * leaves a real account behind. The wizard says so rather than pretending
 * Cancel still undoes anything: past step 1 the only way out is "Finish later",
 * and the rest is reachable from the users table afterwards.
 */
export function AddUserWizard({
  app,
  admin,
  onClose,
  onSaved,
}: {
  app: AppModel;
  admin: AdminConsole;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLingui();
  const [step, setStep] = useState(0);
  // The created user as the API answered with it - `access_policy` included, so
  // step 2 has the role's default policy without a `GET /policies`.
  const [created, setCreated] = useState<UserResponse | null>(null);
  // Step 2's draft lives here, not in the step: step 2 unmounts while step 3
  // is up, and Back has to come back to the edits, not to the role's defaults.
  const [policyDraft, setPolicyDraft] = useState<PolicyDraft | null>(null);
  const [fields] = useState(() =>
    observable({
      username: "",
      email: null as string | null,
      password: "",
      role: null as UserRole | null,
      preferred_language: LocaleTag.sk,
    }),
  );

  /** Closing at any point past step 1 has a created user to report upwards. */
  const close = () => {
    if (created) {
      onSaved();
    }
    onClose();
  };

  async function createUser() {
    const { role } = fields;
    assert(role != null, "Role is required");

    const res = await apiPostUsers({ body: { ...fields, role } });
    const policy = res.data.access_policy;
    const initial = buildInitialFields({
      ...policy,
      locations: toLocations(policy.regions),
    });
    setCreated(res.data);
    setPolicyDraft({ initial, fields: observable({ ...initial }) });
    setStep(1);
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && close()}
      // Step 2 lays out the permissions dialog's sections and needs its card;
      // the other two are ordinary add forms.
      className={step === 1 ? WIDE_DIALOG_CLASS : ADD_DIALOG_CLASS}
      width={step === 1 ? undefined : ADD_DIALOG_WIDTH}
    >
      <DialogHeader
        description={
          created && (
            <Trans>
              The account {created.username} already exists. Leaving now keeps
              it, with whatever is set so far.
            </Trans>
          )
        }
      >
        <Trans>Add new user</Trans>
      </DialogHeader>

      {/* Stays put above the step's scrolling body, which drops its top
          padding: the stepper's own bottom margin already spaces the two. */}
      <div className="shrink-0 px-6 pt-5 max-sm:px-4">
        <Stepper
          current={step}
          label={t`New user steps`}
          steps={[
            <Trans key="account">Account</Trans>,
            <Trans key="permissions">Permissions</Trans>,
            <Trans key="associations">Associations</Trans>,
          ]}
        />
      </div>

      {step === 0 && (
        <Form className="flex flex-col min-h-0 flex-1" onSubmit={createUser}>
          <DialogBody className="pt-0">
            <FormTable dataAdd>
              <FormRow
                label={t`Username (case INsensitive)`}
                data={<TextInput attr="username" model={fields} required />}
                mandatory
              />
              <FormRow
                label={t`Email`}
                data={<TextInput attr="email" model={fields} required />}
                mandatory
              />
              <FormRow
                label={t`Password`}
                data={<PasswordInput attr="password" model={fields} required />}
                mandatory
              />
              <FormRow
                label={t`Role`}
                data={
                  <Select
                    attr="role"
                    values={creatableRoles}
                    model={fields}
                    listTitle={t`Select role`}
                    required
                  />
                }
                mandatory
              />
              <FormRow
                label={t`Preferred language`}
                data={
                  <Select
                    attr="preferred_language"
                    values={supportedLanguages}
                    model={fields}
                    required
                  />
                }
                mandatory
              />
            </FormTable>
          </DialogBody>

          <StepActions
            submit={<Trans>Create user</Trans>}
            secondary={<Trans>Cancel</Trans>}
            onSecondary={close}
          />
        </Form>
      )}

      {step === 1 && created && policyDraft && (
        <PermissionsStep
          admin={admin}
          created={created}
          draft={policyDraft}
          onNext={() => setStep(2)}
          onFinishLater={close}
        />
      )}

      {step === 2 && created && policyDraft && (
        <AssociationsStep
          app={app}
          admin={admin}
          created={created}
          policy={policyDraft.fields}
          onBack={() => setStep(1)}
          onDone={close}
          onFinishLater={close}
        />
      )}
    </Dialog>
  );
}

/**
 * Step 2: the permissions and visibility sections of the permissions dialog,
 * prefilled with what the role already granted, so the admin reviews a policy
 * rather than filling an empty one in.
 *
 * Not an `observer` - the draft is read by the pickers, which are observers
 * themselves.
 */
function PermissionsStep(props: {
  admin: AdminConsole;
  created: UserResponse;
  draft: PolicyDraft;
  onNext: () => void;
  onFinishLater: () => void;
}) {
  const {
    admin,
    created,
    draft: { initial, fields },
  } = props;
  const policy = created.access_policy;
  const { metadata, loadError, retry } = usePermissionsMetadata();

  return (
    <Form
      className="flex flex-col min-h-0 flex-1"
      onSubmit={async () => {
        const body = policyPatchBody(fields, initial);
        // Coming back from step 3 and going forward again re-sends the same
        // patch. A PUT of an unchanged policy is a no-op, so the baseline stays
        // the create response rather than being resnapshotted after each save.
        if (body) {
          await admin.updateAccessPolicy(policy.uuid, body);
        }
        props.onNext();
      }}
    >
      <DialogBody className={PERMISSION_SECTIONS_CLASS}>
        {!metadata ? (
          <PermissionsMetadataFallback
            loadError={loadError}
            retry={retry}
            className="pb-4"
          />
        ) : (
          <>
            <PermissionsPicker
              fields={fields}
              groups={metadata.groups}
              rules={metadata.rules}
            />
            <VisibilityPicker
              fields={fields}
              groups={metadata.groups}
              rules={metadata.rules}
              admin={admin}
              username={created.username}
            />
          </>
        )}
      </DialogBody>

      <StepActions
        submit={<Trans>Next</Trans>}
        secondary={<Trans>Finish later</Trans>}
        onSecondary={props.onFinishLater}
      />
    </Form>
  );
}

/**
 * Step 3: the records the user is tied to, all riding one `PUT /users/{uuid}`.
 *
 * Managed patients are deliberately absent, and the three relations here are
 * not symmetric - checked against a live API, not inferred:
 *
 * - `visible_patients` is *one list* shared by the user and its access policy
 *   (both responses even call it that), so a row here would silently fight the
 *   visibility scope step 2 just wrote. Step 2 owns it, next to the bit that
 *   grants it.
 * - `assigned_physicians` / `assigned_clinics` are *separate* lists from the
 *   policy's `physicians` / `clinics` visibility scope, but not independent of
 *   it: `PUT /users` rejects a physician the user cannot see with `405`. A
 *   role's default policy sees none (`physician` has the bit with an empty
 *   list, `study-physician` and `physician-ppg-app` only managed patients), and
 *   a physician created inline here cannot have been picked in step 2. So
 *   Finish first adds the assigned physicians to the policy, see
 *   {@link physicianVisibilityPatch}.
 */
const AssociationsStep = observer(function AssociationsStep(props: {
  app: AppModel;
  admin: AdminConsole;
  created: UserResponse;
  /** Step 2's draft, as saved when it went on to this step. */
  policy: Fields;
  onBack: () => void;
  onDone: () => void;
  onFinishLater: () => void;
}) {
  const { app, admin, created, policy } = props;
  const [initial] = useState(() => ({
    physicians: [] as string[],
    clinics: [] as string[],
  }));
  const [draft] = useState(() => observable(cloneDeep(initial)));
  const fullVisibility = policy.permissions.includes(
    PermissionBit.VISIBILITY_FULL,
  );

  return (
    <Form
      className="flex flex-col min-h-0 flex-1"
      onSubmit={async () => {
        const policyPatch = physicianVisibilityPatch(policy, draft.physicians);
        if (policyPatch) {
          await admin.updateAccessPolicy(
            created.access_policy.uuid,
            policyPatch,
          );
          // Kept in step 2's draft too, so Back shows them and its next save
          // does not take them away again.
          policy.physician_uuids.push(...(policyPatch.append_physicians ?? []));
          policy.permissions.push(...(policyPatch.add_permissions ?? []));
        }
        const patch = createPatch({ ...draft }, initial);
        if (Object.keys(patch).length > 0) {
          // Not `admin.updateUser`: it ends in a full page reload, which would
          // take the wizard with it before it can close on its own terms.
          await apiPutUsersUuid({
            path: { uuid: created.uuid },
            body: patch,
          });
        }
        props.onDone();
      }}
    >
      <DialogBody className="pt-0">
        <FormTable dataAdd>
          {/* `app` turns on inline creation - the wizard is where a physician
              or clinic that does not exist yet gets made. */}
          <AssignmentRows
            draft={draft}
            admin={admin}
            username={created.username}
            userUuid={created.uuid}
            app={app}
          />
        </FormTable>
        {!fullVisibility && (
          <p className="text-muted-foreground mt-2 text-xs">
            <Trans>
              Assigned physicians are also added to this user's visibility
              scope, so the user can see their patients.
            </Trans>
          </p>
        )}
      </DialogBody>

      <StepActions
        submit={<Trans>Finish</Trans>}
        secondary={<Trans>Finish later</Trans>}
        onSecondary={props.onFinishLater}
        onBack={props.onBack}
      />
    </Form>
  );
});

/**
 * The step-2 draft: the policy as the create response gave it, and the edits
 * accumulating on top. {@link policyPatchBody} diffs the two.
 */
interface PolicyDraft {
  initial: Fields;
  fields: Fields;
}

/**
 * Body for `PUT /policies/{uuid}` that lets the user see each physician it is
 * about to be assigned, or `null` when the policy already does.
 *
 * Full visibility covers every physician. Otherwise a physician not in the
 * policy's list is appended, along with the bit that makes the list count -
 * even when a region or clinic scope would already cover it: matching those
 * here would mean copying the API's check.
 */
function physicianVisibilityPatch(
  policy: Fields,
  physicians: string[],
): UpdateUserAccessPolicySchema | null {
  if (policy.permissions.includes(PermissionBit.VISIBILITY_FULL)) return null;

  const missing = physicians.filter(
    (uuid) => !policy.physician_uuids.includes(uuid),
  );
  if (missing.length === 0) return null;

  return {
    append_physicians: missing,
    ...(!policy.permissions.includes(PermissionBit.VISIBILITY_PHYSICIAN) && {
      add_permissions: [PermissionBit.VISIBILITY_PHYSICIAN],
    }),
  };
}

/**
 * The walk-away / submit pair every step ends with, plus Back where there is a
 * step to go back to. Back sits apart on the left, the way the steps run.
 */
function StepActions(props: {
  submit: ReactNode;
  secondary: ReactNode;
  onSecondary: () => void;
  onBack?: () => void;
}) {
  return (
    <DialogFooter>
      {props.onBack && (
        <Button type="button" onClick={props.onBack} className="sm:mr-auto">
          <Trans>Back</Trans>
        </Button>
      )}
      <Button type="button" onClick={props.onSecondary}>
        {props.secondary}
      </Button>
      <Button variant="primary" type="submit">
        {props.submit}
      </Button>
    </DialogFooter>
  );
}
